import {
  DishAllergen,
  DishDisclaimers,
  DishIngredient,
  DishPreference,
  DishResponse,
  DishTag,
} from "api-types";

import React, { useMemo } from "react";

import { Box, Stack, Typography, styled, useTheme } from "@mui/material";

import {
  CheckCircleRounded,
  InfoRounded,
  NotInterestedRounded,
} from "@mui/icons-material";

import {
  AllergenChipOutlined,
  ChilliIcon,
  PreferenceChipOutlined,
  TagChip,
} from "../../Chip/Chip";
import { DynamicDrawer } from "../../Drawer/DynamicDrawer";
import CloseIcon from "../../Icons/Close";
import KnifeAndForkIcon from "../../Icons/KnifeAndFork";
import { ImagePreview } from "../../ImagePreview/ImagePreview";
import { DishInfoMayContain } from "./DishInfoMayContain";

interface DishInfoDrawerProps {
  /**
   * Restaurant Info
   */
  dish: DishResponse;

  specialIcon?: React.ReactNode;
  highlightColor?: string;

  /**
   * Formatted price in the user's locale
   */
  formattedPrice: string;

  matchedRecommendTags: DishTag[];
  matchedNeutralTags: DishTag[];
  matchedAllergens: DishAllergen[];
  matchedMayContainAllergens: DishAllergen[];
  matchedPreferences: DishPreference[];
  matchedIngredients: DishIngredient[];
  allergenDisclaimers: DishDisclaimers[];
  preferenceDisclaimers: DishDisclaimers[];

  /**
   * Is the drawer open
   */
  open: boolean;

  /**
   * Callback when the drawer is closed
   */
  onClose?: () => void;
  onOpenAllergenIngredients?: () => void;

  showCalories: boolean;
  showMayContains: boolean;
  isIngredientFilteringEnabled?: boolean;
  hasFilteredAllergensOrIngredient: boolean;

  containerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Primary UI component for user interaction
 */
export const DishInfoDrawer = ({
  dish,
  formattedPrice,
  open,
  onClose,
  onOpenAllergenIngredients,
  specialIcon,
  highlightColor,
  matchedPreferences,
  matchedAllergens,
  matchedMayContainAllergens,
  matchedIngredients,
  matchedRecommendTags,
  matchedNeutralTags,
  allergenDisclaimers,
  preferenceDisclaimers,
  showCalories,
  showMayContains,
  hasFilteredAllergensOrIngredient,
  containerRef,
}: DishInfoDrawerProps) => {
  const theme = useTheme();
  const { oscar } = theme.palette;

  const compatibilityType = useMemo(() => {
    // @ts-ignore
    return matchedAllergens?.length > 0
      ? "allergy"
      : matchedPreferences?.length > 0
      ? "preference"
      : "standard";
  }, [matchedPreferences, matchedAllergens]);

  const hasIngredient = !!matchedIngredients.length;

  const hasAllergens =
    !!matchedAllergens.length || !!matchedMayContainAllergens.length;

  const hasOptionalAllergens = useMemo(
    () => !!dish.allergens.find((a) => a.isOptional) && hasAllergens,
    [hasAllergens]
  );

  return (
    <DynamicDrawer
      ref={containerRef}
      drawerType="medium"
      open={open}
      onClose={onClose}
      Header={
        <>
          {dish.imageUrl ? (
            <Box marginBottom={4}>
              <ImagePreview
                imageUrl={dish.imageUrl}
                height="200px"
                width="350px"
                sx={{
                  borderRadius: 0,
                }}
              />
            </Box>
          ) : null}
          <Stack alignItems="center" justifyItems="flex-start" gap={1}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              {specialIcon}
              <KnifeAndForkIcon
                color={highlightColor || oscar.light.icon.dark}
                height={32}
                width={32}
              />
            </Stack>
            <Typography
              color={highlightColor}
              variant="headingRLight"
              textAlign="center"
            >
              {dish.name}
            </Typography>
          </Stack>
        </>
      }
      Footer={
        <CloseIcon
          color={oscar.light.icon.black}
          style={{ cursor: "pointer" }}
          height={32}
          width={32}
          onClick={onClose}
        />
      }
    >
      <Stack alignItems="center" justifyItems="flex-start" gap={1}>
        {formattedPrice ? (
          <Typography
            color={oscar.light.content.content2}
            variant="labelLMedium"
          >
            {formattedPrice}
          </Typography>
        ) : null}
        {!!showCalories && dish.caloriesInKcal && (
          <Typography
            color={oscar.light.content.content2}
            variant="labelLMedium"
          >
            {dish.caloriesInKcal} kcal
          </Typography>
        )}
      </Stack>
      <Box
        display="flex"
        flexDirection="column"
        gap={4}
        alignItems="center"
        width="100%"
      >
        <Box>
          {!!dish.spiceLevel && (
            <Typography variant={"body1"}>
              {ChilliIcon.repeat(dish.spiceLevel)}
            </Typography>
          )}
          {!!matchedNeutralTags.length ? (
            <Box
              alignItems="center"
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              gap={1}
              marginTop={1}
            >
              {matchedNeutralTags.map((tag) => (
                <TagChip key={tag.id} tag={tag} />
              ))}
            </Box>
          ) : null}
        </Box>

        {matchedPreferences.length || matchedRecommendTags.length ? (
          <ChipRow>
            {matchedPreferences.map((preference) => (
              <PreferenceChipOutlined
                key={preference.igfdTaxPreferenceId}
                preferenceName={preference.preferenceName}
              />
            ))}
            {matchedRecommendTags.map((tag) => (
              <TagChip key={tag.id} tag={tag} />
            ))}
          </ChipRow>
        ) : null}

        {dish.description ? (
          <Typography
            color={theme.palette.oscar.light.content.content2}
            variant="bodyRLight"
            textAlign="center"
          >
            {dish.description}
          </Typography>
        ) : null}

        {showMayContains || hasAllergens || hasIngredient ? (
          <>
            {hasOptionalAllergens ? (
              <ChipRow>
                <Box
                  alignItems="center"
                  display="flex"
                  gap={1}
                  sx={({ palette }) => ({
                    backgroundColor: palette.grey[200],
                  })}
                >
                  <InfoRounded
                    sx={({ palette }) => ({
                      color: palette.error.light,
                    })}
                  />
                  <Typography variant={"caption"} textAlign="left">
                    Remove allergen(s) upon request. Ask staff for details.
                  </Typography>
                </Box>
              </ChipRow>
            ) : null}
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              gap={2}
              width="100%"
            >
              {matchedAllergens.length || matchedIngredients.length ? (
                <>
                  <Typography
                    color={oscar.light.content.content1}
                    variant="labelRMedium"
                  >{`${
                    matchedAllergens.length + matchedIngredients.length
                  } Confirmed allergen(s):`}</Typography>
                  <ChipRow justifyContent="center" flexWrap="wrap">
                    {matchedAllergens.map((allergen) => (
                      <AllergenChipOutlined
                        key={allergen.igfdTaxAllergenId}
                        allergenName={
                          allergen.isOptional
                            ? `${allergen.allergenName}: Ask to remove`
                            : allergen.allergenName
                        }
                      />
                    ))}
                    {matchedIngredients.map((ingredient) => (
                      <AllergenChipOutlined
                        key={ingredient.igfdTaxIngredientId}
                        allergenName={ingredient.ingredientName}
                      />
                    ))}
                  </ChipRow>
                </>
              ) : null}
              {matchedAllergens.length ? (
                <Typography
                  color={oscar.light.content.link}
                  variant="buttonS"
                  sx={{ cursor: "pointer" }}
                  onClick={onOpenAllergenIngredients}
                >
                  Related ingredients
                </Typography>
              ) : null}
            </Box>
            {showMayContains && (
              <DishInfoMayContain
                allergenDisclaimers={allergenDisclaimers}
                hasFilteredAllergensOrIngredient={
                  hasFilteredAllergensOrIngredient
                }
                matchedMayContainAllergens={matchedMayContainAllergens}
              />
            )}
          </>
        ) : null}
        {!!preferenceDisclaimers.length &&
          preferenceDisclaimers.map((disclaimer) => (
            <Stack
              gap={1}
              marginBottom={4}
              sx={({ palette }) => ({
                backgroundColor: palette.grey[200],
                borderRadius: 2,
                padding: 2,
              })}
            >
              <Box alignSelf="center" paddingBottom={1}>
                <InfoRounded
                  fontSize="small"
                  sx={{ "&&": { color: "warning.light" } }}
                />
              </Box>
              <Stack gap={2} alignItems="center">
                <PreferenceChipOutlined
                  preferenceName={disclaimer.title}
                  mayNotBe
                />
                <Stack gap={2}>
                  <Typography variant="body2" textAlign="start">
                    {disclaimer.ingredients
                      .map((ingredient) => ingredient.name)
                      .join(", ")}
                  </Typography>
                  <Typography variant="body2" textAlign="start">
                    {disclaimer.text}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          ))}
      </Box>
    </DynamicDrawer>
  );
};

const ChipRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
